import { watch } from "vue";
import { useAuthStore } from "~/stores/useAuthStore";
import { useDaycareStore } from "~/stores/useDaycareStore";
import {
  flushDaycareSyncNow,
  hydrateDaycareFromServerPayload,
  persistDaycareLocalBackup,
} from "~/composables/useDaycareSync";
import { nudgeDaycareAutoRefillIfNeeded } from "~/composables/useDaycareAutoRefill";
import {
  DAYCARE_PROGRESS_STORAGE_KEY_PREFIX,
  loadDaycareProgressBackup,
  mergeDaycareProgressBackupIntoStore,
} from "~/utils/daycareProgressBackup";
import {
  BASE_DAYCARE_SLOTS,
  MAX_EXTRA_DAYCARE_SLOTS,
} from "~/stores/useDaycareStore";
import { usePlayerStore } from "~/stores/usePlayerStore";

const BACKUP_DEBOUNCE_MS = 800;
let backupDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let storageListenerBound = false;
let pagehideListenerBound = false;
let crossTabWatcherStop: (() => void) | null = null;

function onPageHideFlush() {
  flushDaycareProgressBackupNow();
  void flushDaycareSyncNow();
}

function onVisibilityHiddenFlush() {
  if (document.visibilityState === "hidden") onPageHideFlush();
}

function daycareMaxSlots() {
  const player = usePlayerStore();
  return (
    BASE_DAYCARE_SLOTS +
    Math.min(MAX_EXTRA_DAYCARE_SLOTS, player.extraDaycareSlots ?? 0)
  );
}

function scheduleDaycareProgressBackup() {
  if (backupDebounceTimer) clearTimeout(backupDebounceTimer);
  backupDebounceTimer = setTimeout(() => {
    backupDebounceTimer = null;
    persistDaycareLocalBackup();
  }, BACKUP_DEBOUNCE_MS);
}

export function flushDaycareProgressBackupNow() {
  if (backupDebounceTimer) {
    clearTimeout(backupDebounceTimer);
    backupDebounceTimer = null;
  }
  persistDaycareLocalBackup();
}

function onDaycareStorageEvent(e: StorageEvent) {
  if (!e.key?.startsWith(DAYCARE_PROGRESS_STORAGE_KEY_PREFIX) || !e.newValue)
    return;
  const auth = useAuthStore();
  if (!auth.gameLoaded) return;
  const userId = auth.user?.id;
  if (!userId) return;
  const backup = loadDaycareProgressBackup(userId);
  if (!backup) return;
  const daycare = useDaycareStore();
  const changed = mergeDaycareProgressBackupIntoStore(
    {
      slots: daycare.slots,
      lastDeposited: daycare.lastDeposited,
      reserveQueue: daycare.reserveQueue,
      reserveFillPending: daycare.reserveFillPending,
      setSlots: (next) => daycare.setSlots(next),
      setLastDeposited: (next) => daycare.setLastDeposited(next),
      setReserveQueue: (next) => daycare.setReserveQueue(next),
      setReserveFillPending: (n) => daycare.setReserveFillPending(n),
    },
    backup,
    daycareMaxSlots(),
  );
  if (changed) nudgeDaycareAutoRefillIfNeeded();
}

/** Backup local debouncé + sync entre onglets via `storage`. */
export function bindDaycareCrossTabSync() {
  if (typeof window === "undefined") return;
  const daycare = useDaycareStore();
  const auth = useAuthStore();

  crossTabWatcherStop = watch(
    () => [
      daycare.slots
        .map((s) => `${s.slug}:${s.isShiny}:${s.stars}:${s.damageDealt}`)
        .join("|"),
      daycare.lastDeposited.length,
      daycare.reserveQueue.length,
      daycare.reserveFillPending,
      daycare.pendingRevision,
    ],
    () => {
      if (!auth.gameLoaded) return;
      scheduleDaycareProgressBackup();
    },
  );

  if (!storageListenerBound) {
    storageListenerBound = true;
    window.addEventListener("storage", onDaycareStorageEvent);
  }
  if (!pagehideListenerBound) {
    pagehideListenerBound = true;
    window.addEventListener("pagehide", onPageHideFlush);
    document.addEventListener("visibilitychange", onVisibilityHiddenFlush);
  }
}

export function unbindDaycareCrossTabSync() {
  if (crossTabWatcherStop) {
    crossTabWatcherStop();
    crossTabWatcherStop = null;
  }
  if (backupDebounceTimer) {
    clearTimeout(backupDebounceTimer);
    backupDebounceTimer = null;
  }
  if (typeof window !== "undefined") {
    if (storageListenerBound) {
      window.removeEventListener("storage", onDaycareStorageEvent);
      storageListenerBound = false;
    }
    if (pagehideListenerBound) {
      window.removeEventListener("pagehide", onPageHideFlush);
      document.removeEventListener("visibilitychange", onVisibilityHiddenFlush);
      pagehideListenerBound = false;
    }
  }
}

/** Fusionne le backup local courant dans le store (retourne true si mutation appliquée). */
export function hydrateDaycareFromLocalProgressBackup(): boolean {
  const auth = useAuthStore();
  const userId = auth.user?.id;
  if (!auth.gameLoaded || !userId) return false;
  const backup = loadDaycareProgressBackup(userId);
  if (!backup) return false;
  const daycare = useDaycareStore();
  const changed = mergeDaycareProgressBackupIntoStore(
    {
      slots: daycare.slots,
      lastDeposited: daycare.lastDeposited,
      reserveQueue: daycare.reserveQueue,
      reserveFillPending: daycare.reserveFillPending,
      setSlots: (next) => daycare.setSlots(next),
      setLastDeposited: (next) => daycare.setLastDeposited(next),
      setReserveQueue: (next) => daycare.setReserveQueue(next),
      setReserveFillPending: (n) => daycare.setReserveFillPending(n),
    },
    backup,
    daycareMaxSlots(),
  );
  if (changed) nudgeDaycareAutoRefillIfNeeded();
  return changed;
}

/** Flush avant de céder le leadership multi-onglet. */
export async function flushDaycareProgressBeforeStandby() {
  flushDaycareProgressBackupNow();
  await flushDaycareSyncNow();
}
